import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { ONBOARDING_V2 } from '../onboardingV2Routes';
import { getUserPlan, planAllowsMultiBrand } from '../../../lib/planUtils';
import { enGuion } from '../../../lib/onboardingMarca/guion';
import { hayQueReiniciar, leerEstado, limpiarEstado } from '../../../lib/onboardingMarca/estado';
import { buscarMarca } from '../../../lib/onboardingMarca/marca';
import {
  enMarcaNueva,
  MARCA_NUEVA,
  NOMBRE_DEL_PASO_DE_MARCA_NUEVA,
  PASOS_DE_MARCA_NUEVA,
} from '../../../lib/onboardingMarca/marcaNueva';
import { Vitrina } from './Vitrina';
import './onboardingMarca.css';
import './marcoDesktop.css';

const PASOS = [
  ONBOARDING_V2.setup,
  ONBOARDING_V2.setupPreguntas,
  ONBOARDING_V2.setupMarca,
  // El canal va justo después de la marca (ADR-2026-10-02-onboarding-v2-canal-despues-de-la-marca).
  ONBOARDING_V2.setupCanal,
  ONBOARDING_V2.setupPiezas,
  ONBOARDING_V2.setupListo,
];

/** Lo que dice la barra al lado de "Paso n de N". */
const NOMBRE_DEL_PASO: Record<(typeof PASOS)[number], string> = {
  [ONBOARDING_V2.setup]: 'Tu web',
  [ONBOARDING_V2.setupPreguntas]: 'Tu negocio',
  [ONBOARDING_V2.setupMarca]: 'Tu marca',
  [ONBOARDING_V2.setupCanal]: 'Canal de venta',
  [ONBOARDING_V2.setupPiezas]: 'Primeras piezas',
  [ONBOARDING_V2.setupListo]: 'Listo',
};

/**
 * Marco del onboarding de marca (ADR-2026-09-28-onboarding-v2-etapa-1): arriba, en qué
 * paso va y cómo se llama, sobre el fondo de la pantalla; cada pantalla entra con un
 * fundido. Sin logo ni "Volver" (ADR-2026-10-02-onboarding-v2-pasos-integrados): el
 * paso 1 ya no desloguea desde acá.
 */
export function OnboardingMarcaLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useUserGroup();
  // "Añadir nueva marca" usa este mismo marco con sus tres pasos (ADR-2026-10-02-marca-nueva-como-el-onboarding).
  const nueva = enMarcaNueva(pathname);
  const pasos: readonly string[] = nueva ? PASOS_DE_MARCA_NUEVA : PASOS;
  const nombres: Record<string, string> = nueva ? NOMBRE_DEL_PASO_DE_MARCA_NUEVA : NOMBRE_DEL_PASO;
  const paso = Math.max(0, pasos.indexOf(pathname));
  const enTablero = pathname === ONBOARDING_V2.setupMarca || pathname === MARCA_NUEVA.marca;

  // Sumar una marca es de los planes pagos, como en la pantalla vieja (`planAllowsMultiBrand`).
  useEffect(() => {
    if (!nueva || enGuion()) return;
    let activo = true;
    void getUserPlan().then((plan) => {
      if (activo && !planAllowsMultiBrand(plan)) navigate('/subscription', { replace: true });
    });
    return () => {
      activo = false;
    };
  }, [nueva, navigate]);

  // Sin sesión, a la bienvenida. Con sesión, el onboarding guardado tiene que ser de ESTA
  // cuenta: si lo dejó otra (una sesión que venció sin pasar por el logout, y después entró
  // otra persona), se tira y se arranca de cero, en vez de seguir con su empresa y su marca
  // (ADR-2026-10-07-el-onboarding-guardado-se-valida-contra-la-sesion).
  useEffect(() => {
    let activo = true;
    void (async () => {
      const { data } = await supabase.auth.getSession();
      if (!activo) return;
      if (!data.session) return navigate(ONBOARDING_V2.bienvenida, { replace: true });
      const guardado = leerEstado(null);
      if (!guardado || enGuion()) return;
      const { data: perfil, error } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', data.session.user.id)
        .maybeSingle();
      if (!activo) return;
      const companyIdDeLaSesion = error ? undefined : ((perfil as { id_company?: string | null } | null)?.id_company ?? null);
      // Si la empresa no se pudo leer, decide la marca guardada: la de otra cuenta no existe
      // para esta sesión (ADR-2026-10-07-una-marca-que-no-existe-reinicia-el-onboarding).
      const marca =
        companyIdDeLaSesion === undefined && guardado.brandId ? (await buscarMarca(guardado.brandId)).estado : undefined;
      if (!activo || !hayQueReiniciar({ guardado, companyIdDeLaSesion, marca })) return;
      limpiarEstado();
      navigate(nueva ? MARCA_NUEVA.link : ONBOARDING_V2.setup, { replace: true });
    })();
    return () => {
      activo = false;
    };
    // Se valida una vez al entrar al flujo: el marco queda montado entre paso y paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <div className="om-marco min-h-[100dvh] bg-[#FAFAFA] text-[#18181B]">
      {/* En desktop el paso va en un recuadro blanco con borde azul, sobre el fondo de la
          marca oscurecido (ADR-2026-10-02-onboarding-v2-recuadro-desktop). En el teléfono,
          `om-fondo-desktop` no se ve y `om-tarjeta-paso` no tiene estilo. */}
      <div className="om-fondo-desktop" aria-hidden="true" />
      {/* En desktop, a los costados: imágenes creadas con Atlas y lo que dicen las marcas
          (ADR-2026-10-02-onboarding-v2-vitrina-desktop). No en el tablero, que usa el ancho. */}
      {!enTablero && <Vitrina />}
      <div className={`om-tarjeta-paso ${enTablero ? 'om-tarjeta-paso--ancha' : ''}`}>
      {/* Sin fondo ni borde: los pasos van sobre la pantalla misma (el fondo de Cumbre
          ocupa la ventana entera). No es sticky: transparente, el contenido le pasaría
          por debajo. */}
      <header className="relative z-20 mx-auto flex h-[var(--om-barra)] max-w-[640px] flex-col justify-end gap-2.5 px-4 pb-5">
        <p className="flex items-baseline justify-between text-[13px] font-semibold leading-[18px]">
          <span className="text-[#1644EB]">
            Paso {paso + 1} <span className="text-[#98A1C0]">de {pasos.length}</span>
          </span>
          <span className="flex items-baseline gap-3">
            <span className="text-[#4B5275]">{nombres[pasos[paso]]}</span>
            {/* Adentro de la app hay que poder irse: la marca queda sin sumar. */}
            {nueva && (
              <button type="button" onClick={() => navigate('/', { replace: true })} className="om-salir">
                Salir
              </button>
            )}
          </span>
        </p>
        <ol className="flex gap-1.5" aria-hidden="true">
          {pasos.map((p, i) => (
            <li key={p} className={`om-paso ${i < paso ? 'is-hecho' : i === paso ? 'is-actual' : ''}`} />
          ))}
        </ol>
      </header>
      <main key={pathname} className="om-pantalla">
        <Outlet />
      </main>
      </div>
    </div>
  );
}
